/**
 * Hyperframes format helpers — pure functions, no React, no Remotion.
 *
 * Mirrors what `src/primitives/FormatLayout.tsx` provided to the legacy
 * Remotion pipeline, but as pure data + helpers usable from Node builders.
 */
export type FormatId = "9:16" | "1:1" | "16:9";

export const formatDimensions: Record<FormatId, { width: number; height: number }> = {
  "9:16": { width: 1080, height: 1920 },
  "1:1": { width: 1080, height: 1080 },
  "16:9": { width: 1920, height: 1080 },
};

export function detectFormat(width: number, height: number): FormatId {
  if (Math.abs(width / height - 9 / 16) < 0.05) return "9:16";
  if (Math.abs(width / height - 1) < 0.05) return "1:1";
  return "16:9";
}

export type PlatformId = "tiktok" | "reels" | "shorts" | "stories" | "general";

export type SafeZone = {
  top: number;
  bottom: number;
  left: number;
  right: number;
  inner: number;
};

const SAFE_9_16: Record<PlatformId, SafeZone> = {
  tiktok: { top: 230, bottom: 460, left: 70, right: 200, inner: 36 },
  reels: { top: 240, bottom: 420, left: 70, right: 220, inner: 36 },
  shorts: { top: 220, bottom: 380, left: 80, right: 200, inner: 36 },
  stories: { top: 260, bottom: 320, left: 70, right: 90, inner: 36 },
  general: { top: 220, bottom: 380, left: 80, right: 180, inner: 36 },
};

const SAFE_1_1: Record<PlatformId, SafeZone> = {
  tiktok: { top: 110, bottom: 110, left: 110, right: 110, inner: 32 },
  reels: { top: 110, bottom: 110, left: 110, right: 110, inner: 32 },
  shorts: { top: 110, bottom: 110, left: 110, right: 110, inner: 32 },
  stories: { top: 110, bottom: 110, left: 110, right: 110, inner: 32 },
  general: { top: 100, bottom: 100, left: 100, right: 100, inner: 32 },
};

const SAFE_16_9: Record<PlatformId, SafeZone> = {
  tiktok: { top: 100, bottom: 100, left: 140, right: 140, inner: 32 },
  reels: { top: 100, bottom: 100, left: 140, right: 140, inner: 32 },
  shorts: { top: 100, bottom: 100, left: 140, right: 140, inner: 32 },
  stories: { top: 100, bottom: 100, left: 140, right: 140, inner: 32 },
  general: { top: 90, bottom: 90, left: 140, right: 140, inner: 32 },
};

export function getSafeZone(format: FormatId, platform: PlatformId = "general"): SafeZone {
  if (format === "9:16") return SAFE_9_16[platform];
  if (format === "1:1") return SAFE_1_1[platform];
  return SAFE_16_9[platform];
}

/** Mirror of `useScale()` but pure — usable in Node builders. */
export function scaleFor(
  format: FormatId,
  cfg: { base: number; short?: number; square?: number; wide?: number },
): number {
  if (format === "9:16") return cfg.short ?? cfg.base;
  if (format === "1:1") return cfg.square ?? cfg.base;
  return cfg.wide ?? cfg.base;
}

/** Returns the canvas dimensions for a format. */
export function dimensionsFor(format: FormatId): { width: number; height: number } {
  return formatDimensions[format];
}

/** Default content padding (matches `SafeArea` non-platform branch). */
export function defaultPad(format: FormatId): number {
  if (format === "9:16") return 90;
  if (format === "1:1") return 80;
  return 110;
}
